import React, { createContext, useContext, useState, useMemo, ReactNode } from 'react';

export type ViewMode = 'site_admin' | 'facility' | 'collector' | 'facility_collector';

interface SimulatedAccess {
  hasFacilityAccess: boolean;
  hasCollectorAccess: boolean;
  isSiteAdmin: boolean;
}

interface AdminViewContextType {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  isSimulating: boolean;
  simulatedAccess: SimulatedAccess;
}

const AdminViewContext = createContext<AdminViewContextType | undefined>(undefined);

export const AdminViewProvider = ({ children }: { children: ReactNode }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('site_admin');

  const isSimulating = viewMode !== 'site_admin';

  const simulatedAccess = useMemo<SimulatedAccess>(() => {
    switch (viewMode) {
      case 'site_admin':
        return { hasFacilityAccess: true, hasCollectorAccess: true, isSiteAdmin: true };
      case 'facility':
        return { hasFacilityAccess: true, hasCollectorAccess: false, isSiteAdmin: false };
      case 'collector':
        return { hasFacilityAccess: false, hasCollectorAccess: true, isSiteAdmin: false };
      case 'facility_collector':
        return { hasFacilityAccess: true, hasCollectorAccess: true, isSiteAdmin: false };
      default:
        return { hasFacilityAccess: true, hasCollectorAccess: true, isSiteAdmin: true };
    }
  }, [viewMode]);

  return (
    <AdminViewContext.Provider value={{ viewMode, setViewMode, isSimulating, simulatedAccess }}>
      {children}
    </AdminViewContext.Provider>
  );
};

export const useAdminViewContext = () => {
  const context = useContext(AdminViewContext);
  if (!context) {
    // Return default non-simulating state when used outside provider
    return {
      viewMode: 'site_admin' as ViewMode,
      setViewMode: () => {},
      isSimulating: false,
      simulatedAccess: { hasFacilityAccess: true, hasCollectorAccess: true, isSiteAdmin: true }
    };
  }
  return context;
};
